/**
 * Footer Component (Server)
 *
 * Diamond Architecture: Server Component for site-wide footer.
 * Static content with links - no client-side interactivity needed.
 *
 * Layout:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │  Support Links  │  Explore Links  │  Company Links              │
 * ├─────────────────────────────────────────────────────────────────┤
 * │  © 2025         │  Terms | Privacy│  LinkedIn Facebook Instagram│
 * └─────────────────────────────────────────────────────────────────┘
 *
 * Matches Rails: layouts/_footer.html.erb
 *
 * ## Why it takes a `width`
 *
 * Because `NavbarShell` does, and for the same reason: the column a site band's
 * content takes is a property of PAGE CHROME — the page and its bands share one
 * column — not of the band. This file hard-coded `mx-auto max-w-7xl`, which was
 * safe only by coincidence: every `width: 'bleed'` row happens to have
 * `footer: false` today, and a jsdom assertion said so
 * (`PageShell.react.test.tsx`). A coincidence asserted by a test is the class
 * this whole shell exists to close, so the footer reads the column instead.
 */
import { useTranslations } from '@/shared/i18n';
import { Link } from '@/shared/i18n/routing';
import { routes, type Route } from '@letsgethome/routes';
import type { LocalizedText } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';

import type { NavbarWidth } from './NavbarShell';

// Inline SVG social icons — replaces deprecated lucide-react brand icons.
// Standard 24×24 viewBox, currentColor fill for Tailwind text-* classes.
function LinkedinIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.88 0 1.441 1.441 0 012.88 0z" />
    </svg>
  );
}

// Footer link sections (matches Rails footer_helper.rb)
const FOOTER_SECTIONS = {
  support: [
    { href: routes.help(), labelKey: 'helpCenter' },
    { href: routes.contact(), labelKey: 'contactSupport' },
    { href: routes.feedback(), labelKey: 'giveFeedback' },
  ],
  // The site-wide inbound edge for the browse hubs. `/districts`, `/market`
  // and `/tools` are sitemap-enumerated and were reachable from NOWHERE on
  // the site — a page with no inbound internal link earns minimal crawl
  // priority and inherits no authority, however complete the sitemap is.
  // `/transactions` had one deep link and no site-wide one.
  explore: [
    { href: routes.listings.rent(), labelKey: 'rentProperty' },
    { href: routes.listings.buy(), labelKey: 'buyProperty' },
    { href: routes.estates.index(), labelKey: 'estates' },
    { href: routes.districts.index(), labelKey: 'districts' },
    { href: routes.transactions(), labelKey: 'transactions' },
    { href: routes.market(), labelKey: 'market' },
    { href: routes.tools.index(), labelKey: 'tools' },
  ],
  company: [
    { href: routes.about(), labelKey: 'aboutUs' },
    { href: routes.blog.index(), labelKey: 'blog' },
    { href: routes.services(), labelKey: 'services' },
    { href: routes.partners.index(), labelKey: 'partners' },
  ],
} as const;

const LEGAL_LINKS = [
  { href: routes.terms(), labelKey: 'terms' },
  { href: routes.privacy(), labelKey: 'privacy' },
] as const;

const SOCIAL_LINKS = [
  {
    href: 'https://www.linkedin.com/company/letsgethome',
    label: 'LinkedIn',
    Icon: LinkedinIcon,
  },
  {
    href: 'https://www.facebook.com/letsgethome.hk',
    label: 'Facebook',
    Icon: FacebookIcon,
  },
  {
    href: 'https://instagram.com/letsgethome.hk',
    label: 'Instagram',
    Icon: InstagramIcon,
  },
] as const;

function FooterLinkColumn({
  title,
  links,
}: {
  title: LocalizedText;
  links: Array<{ href: Route; label: LocalizedText }>;
}) {
  return (
    <div className="space-y-3">
      {/* ★ h2, NOT h3, and the difference is a real WCAG defect that a
          third-party script was masking (#6800).

          The footer's columns are top-level sections of the page — nothing
          encloses them — so `h3` SKIPS a level wherever the page's own outline
          stops at `h1`. `/listings/rent` is exactly that page: the hub is a
          full-screen map/list UI whose only heading is an `sr-only` `h1`, and
          the listing cards carry no headings at all. Its outline was therefore
          h1 → h3 and axe's `heading-order` should always have refused it.

          It did not, because until `//web:lighthouse` was DNS-sealed the
          audited browser still reached the public internet, and the Google
          Maps SDK injects its own heading into the page — so a
          third-party script was supplying the missing level. Measured
          2026-09-11 on this PR's own head: sealing the hermetic browser moved
          `/listings/rent` from a11y 0.87 to 0.86, the delta being exactly this
          `heading-order` (weight 3); the other three audited URLs, which have
          their own `h2`s, were unchanged at 0.91/0.91/0.90.

          `h2` is correct on every page, not just that one: axe only refuses a
          heading that SKIPS a level going down, so a footer `h2` after an `h1`
          or after deeper main-content headings is valid either way. All
          heading levels share one rule in `globals.css` (`h1..h6 { font-medium
          text-foreground }`) and the size comes from `text-base`, so this is
          pixel-identical — the pixel baselines and their provenance witnesses
          are untouched. */}
      <h2 className="text-base">{title}</h2>
      <ul className="space-y-2">
        {links.map((link) => (
          <li key={link.href}>
            <Link href={link.href} className="text-sm hover:text-foreground transition-colors">
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer({ width }: { width: NavbarWidth }) {
  const t = useTranslations('nav.footer');
  // #8914 — the third-party notices link is titled by the ONE cross-platform
  // key the mobile Licenses screen already uses (`legal.licenses`).
  const tLegal = useTranslations('legal');

  return (
    <footer className="border-t border-border bg-muted text-muted-foreground">
      <div className={cn('px-4 py-8 md:py-12', width === 'contained' && 'mx-auto max-w-7xl')}>
        <div className="space-y-8">
          {/* Link Sections Grid */}
          <div className="grid gap-8 md:grid-cols-3">
            <FooterLinkColumn
              title={t('groups.support')}
              links={FOOTER_SECTIONS.support.map((link) => ({
                href: link.href,
                label: t(`links.${link.labelKey}`),
              }))}
            />
            <FooterLinkColumn
              title={t('groups.explore')}
              links={FOOTER_SECTIONS.explore.map((link) => ({
                href: link.href,
                label: t(`links.${link.labelKey}`),
              }))}
            />
            <FooterLinkColumn
              title={t('groups.company')}
              links={FOOTER_SECTIONS.company.map((link) => ({
                href: link.href,
                label: t(`links.${link.labelKey}`),
              }))}
            />
          </div>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Bottom Row */}
          <div className="flex flex-col items-center gap-4 text-sm md:flex-row md:justify-between">
            {/* Copyright */}
            <div className="text-center md:text-left">
              © {new Date().getFullYear()} {t('companyName')}
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-4">
              {LEGAL_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="hover:text-foreground transition-colors"
                >
                  {t(`legal.${link.labelKey}`)}
                </Link>
              ))}
              <Link href={routes.licenses()} className="hover:text-foreground transition-colors">
                {tLegal('licenses')}
              </Link>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${t('visitOn')} ${label}`}
                  className="hover:text-foreground transition-colors"
                >
                  <Icon className="size-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
